// Test execution orchestrator
import { runWithTimeout, formatDuration } from './testHelpers';

/**
 * Test result statuses
 */
export const TEST_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  PASS: 'pass',
  FAIL: 'fail',
  WARN: 'warn',
  SKIP: 'skip',
};

/**
 * Run mode options
 */
export const RUN_MODE = {
  SAFE: 'safe',           // Only non-destructive, non-AI tests
  ALL: 'all',             // All tests including destructive (with cleanup)
  AI: 'ai',               // Include AI tests (costs money)
  SINGLE: 'single',       // Single test
  CATEGORY: 'category',   // Single category
};

/**
 * Create initial test state for a category
 * @param {Array} tests - Test definitions
 * @returns {Object} Test state map
 */
export const createInitialTestState = (tests) => {
  const state = {};
  tests.forEach(test => {
    state[test.id] = {
      status: TEST_STATUS.PENDING,
      message: null,
      details: null,
      duration: null,
      error: null,
    };
  });
  return state;
};

/**
 * Run a single test
 * @param {Object} test - Test definition
 * @param {Object} context - Test context (auth, data, offline)
 * @param {Function} onUpdate - Callback for status updates
 * @returns {Promise<Object>} Test result
 */
export const runSingleTest = async (test, context, onUpdate) => {
  const startTime = Date.now();

  // Update to running
  onUpdate?.(test.id, { status: TEST_STATUS.RUNNING });

  try {
    const result = await runWithTimeout(
      () => test.run(context),
      test.timeout || 5000,
      test.name
    );

    const duration = Date.now() - startTime;

    const finalResult = {
      status: result.status || TEST_STATUS.PASS,
      message: result.message || 'Test completed',
      details: result.details || null,
      duration,
    };

    onUpdate?.(test.id, finalResult);
    return finalResult;

  } catch (error) {
    const duration = Date.now() - startTime;

    const failResult = {
      status: TEST_STATUS.FAIL,
      message: error.message || 'Unknown error',
      details: null,
      duration,
      error: error.stack,
    };

    onUpdate?.(test.id, failResult);
    return failResult;
  }
};

/**
 * Run all tests in a category
 * @param {Object} category - Category with tests array
 * @param {Object} context - Test context
 * @param {Object} options - Run options
 * @param {Function} onUpdate - Callback for status updates
 * @returns {Promise<Object>} Category results
 */
export const runCategory = async (category, context, options = {}, onUpdate) => {
  const { runMode = RUN_MODE.SAFE } = options;
  const results = {};

  for (const test of category.tests) {
    // Skip logic based on run mode
    if (runMode === RUN_MODE.SAFE) {
      if (test.destructive) {
        results[test.id] = {
          status: TEST_STATUS.SKIP,
          message: 'Skipped (destructive test)',
        };
        onUpdate?.(test.id, results[test.id]);
        continue;
      }
      if (test.aiCost) {
        results[test.id] = {
          status: TEST_STATUS.SKIP,
          message: 'Skipped (AI cost)',
        };
        onUpdate?.(test.id, results[test.id]);
        continue;
      }
    }

    if (runMode !== RUN_MODE.AI && test.aiCost) {
      results[test.id] = {
        status: TEST_STATUS.SKIP,
        message: 'Skipped (enable AI mode)',
      };
      onUpdate?.(test.id, results[test.id]);
      continue;
    }

    results[test.id] = await runSingleTest(test, context, onUpdate);
  }

  return results;
};

/**
 * Run all tests across all categories
 * @param {Array} categories - All test categories
 * @param {Object} context - Test context
 * @param {Object} options - Run options
 * @param {Function} onUpdate - Callback for status updates
 * @param {Function} onCategoryComplete - Callback when category completes
 * @returns {Promise<Object>} All results
 */
export const runAllTests = async (categories, context, options = {}, onUpdate, onCategoryComplete) => {
  const allResults = {};

  for (const category of categories) {
    const categoryResults = await runCategory(category, context, options, onUpdate);
    allResults[category.id] = categoryResults;
    onCategoryComplete?.(category.id, categoryResults);
  }

  return allResults;
};

/**
 * Calculate summary statistics from results
 * @param {Object} results - Test results by category
 * @returns {Object} Summary stats
 */
export const calculateSummary = (results) => {
  let pass = 0;
  let fail = 0;
  let warn = 0;
  let skip = 0;
  let pending = 0;
  let running = 0;
  let totalDuration = 0;

  Object.values(results).forEach(categoryResults => {
    Object.values(categoryResults).forEach(result => {
      switch (result.status) {
        case TEST_STATUS.PASS:
          pass++;
          break;
        case TEST_STATUS.FAIL:
          fail++;
          break;
        case TEST_STATUS.WARN:
          warn++;
          break;
        case TEST_STATUS.SKIP:
          skip++;
          break;
        case TEST_STATUS.PENDING:
          pending++;
          break;
        case TEST_STATUS.RUNNING:
          running++;
          break;
      }
      if (result.duration) {
        totalDuration += result.duration;
      }
    });
  });

  const total = pass + fail + warn + skip + pending + running;

  return {
    pass,
    fail,
    warn,
    skip,
    pending,
    running,
    total,
    totalDuration,
    formattedDuration: formatDuration(totalDuration),
    overallStatus: fail > 0 ? TEST_STATUS.FAIL : warn > 0 ? TEST_STATUS.WARN : TEST_STATUS.PASS,
  };
};

/**
 * Calculate summary for a single category
 * @param {Object} categoryResults - Results for one category
 * @returns {Object} Category summary
 */
export const calculateCategorySummary = (categoryResults) => {
  if (!categoryResults) return { pass: 0, fail: 0, warn: 0, skip: 0, total: 0 };

  let pass = 0, fail = 0, warn = 0, skip = 0;

  Object.values(categoryResults).forEach(result => {
    switch (result.status) {
      case TEST_STATUS.PASS: pass++; break;
      case TEST_STATUS.FAIL: fail++; break;
      case TEST_STATUS.WARN: warn++; break;
      case TEST_STATUS.SKIP: skip++; break;
    }
  });

  return { pass, fail, warn, skip, total: pass + fail + warn + skip };
};

/**
 * Filter tests by criteria
 * @param {Array} tests - Test definitions
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered tests
 */
export const filterTests = (tests, filters = {}) => {
  return tests.filter(test => {
    if (filters.destructive === false && test.destructive) return false;
    if (filters.aiCost === false && test.aiCost) return false;
    if (filters.ids && !filters.ids.includes(test.id)) return false;
    return true;
  });
};

/**
 * Get tests that should be included in safe mode
 * @param {Array} categories - All categories
 * @returns {Array} Safe tests with category info
 */
export const getSafeTests = (categories) => {
  const safeTests = [];

  categories.forEach(category => {
    category.tests.forEach(test => {
      if (!test.destructive && !test.aiCost) {
        safeTests.push({
          ...test,
          categoryId: category.id,
          categoryName: category.name,
        });
      }
    });
  });

  return safeTests;
};

/**
 * Get total test count by mode
 * @param {Array} categories - All categories
 * @param {string} runMode - Run mode
 * @returns {Object} Count breakdown
 */
export const getTestCounts = (categories, runMode = RUN_MODE.SAFE) => {
  let safe = 0;
  let destructive = 0;
  let ai = 0;

  categories.forEach(category => {
    category.tests.forEach(test => {
      if (test.destructive) {
        destructive++;
      } else if (test.aiCost) {
        ai++;
      } else {
        safe++;
      }
    });
  });

  let runnable;
  switch (runMode) {
    case RUN_MODE.SAFE:
      runnable = safe;
      break;
    case RUN_MODE.AI:
      runnable = safe + ai;
      break;
    case RUN_MODE.ALL:
      runnable = safe + destructive + ai;
      break;
    default:
      runnable = safe;
  }

  return { safe, destructive, ai, total: safe + destructive + ai, runnable };
};

export default {
  TEST_STATUS,
  RUN_MODE,
  createInitialTestState,
  runSingleTest,
  runCategory,
  runAllTests,
  calculateSummary,
  calculateCategorySummary,
  filterTests,
  getSafeTests,
  getTestCounts,
};
